import { z } from 'zod'
import { JOBCARD_ACTIONS, JOBCARD_STATUSES } from '../jobcard-status.js'
import type { Margin, Totals } from '../vat.js'
import { carInputSchema, carSnapshotSchema } from './car.js'
import { lineKindSchema } from './catalog.js'
import type { Client } from './client.js'
import type { DocumentSummary } from './document.js'
import type { TimelineSentOutBrief } from './timeline.js'
import { paymentMethodSchema } from './payment-method.js'
import {
  basisPointsSchema,
  idSchema,
  isoDateTimeSchema,
  milliSchema,
  minorNonNegativeSchema,
  optionalText,
  text,
} from './common.js'

export const jobcardStatusSchema = z.enum(JOBCARD_STATUSES)
export const jobcardActionSchema = z.enum(JOBCARD_ACTIONS)

export const jobcardLineInputSchema = z.object({
  kind: lineKindSchema,
  name: text(200),
  partNumber: optionalText(80),
  /** Services are sent as 1000 (one unit); the UI hides the field for them. */
  quantityMilli: milliSchema.default(1000),
  costMinor: minorNonNegativeSchema.nullable().default(null),
  rateMinor: minorNonNegativeSchema,
  /** Set when the line came from a catalogue suggestion. Provenance only. */
  catalogItemId: idSchema.nullable().default(null),
  /**
   * Internal-only: left off the customer's quotation, invoice and portal
   * page, and out of what they are billed. Visible to staff (and counted in
   * cost/margin) as usual. Off by default.
   */
  hiddenFromCustomer: z.boolean().optional(),
})
export type JobcardLineInput = z.infer<typeof jobcardLineInputSchema>

export const jobcardLineSchema = jobcardLineInputSchema.extend({
  id: idSchema,
  jobcardId: idSchema,
  sortOrder: z.number().int().min(0),
})
export type JobcardLine = z.infer<typeof jobcardLineSchema>

/**
 * How the car got onto the jobcard.
 * - `existing`: pick one of the client's cars by id.
 * - `manual`: type the details. `saveToClient` promotes it into their garage,
 *   which is the checkbox in the editor.
 */
export const jobcardCarSelectionSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('existing'), carId: idSchema }),
  z.object({
    mode: z.literal('manual'),
    car: carInputSchema.omit({ clientId: true }),
    saveToClient: z.boolean().default(false),
  }),
])
export type JobcardCarSelection = z.infer<typeof jobcardCarSelectionSchema>

/**
 * A payment link staff paste in, rendered as a navigation target on the
 * customer portal — the one internet-facing page in this app.
 *
 * Restricted to http/https at the SOURCE so a `javascript:` or `data:` URL can
 * never be stored, let alone clicked. The portal re-checks before rendering:
 * this value predates the rule for any row saved earlier, and defence in depth
 * costs nothing here.
 */
export const paymentUrlSchema = z
  .string()
  .trim()
  .max(500)
  .transform((value) => (value === '' ? null : value))
  .nullable()
  .refine((value) => value === null || /^https?:\/\//i.test(value), {
    message: 'Payment link must start with http:// or https://',
  })

export const jobcardCreateSchema = z.object({
  /**
   * Null while the owner is unknown — a car arrives at the workshop and nobody
   * knows whose it is yet. A customer becomes mandatory only when a document is
   * issued; see `transitionRequiresClient`.
   */
  clientId: idSchema.nullable().default(null),
  car: jobcardCarSelectionSchema,
  complaint: optionalText(2000),
  notes: optionalText(2000),
  odometerKm: z.number().int().min(0).max(10_000_000).nullable().default(null),
  /** Omitted means "use the settings default". */
  vatEnabled: z.boolean().optional(),
  /** Flat amount off the bill in minor units, applied before VAT. */
  discountMinor: minorNonNegativeSchema.default(0),
  /** Customer's order or LPO reference. */
  orderRef: z.string().nullable().optional(),
  /** Appointment date and time. ISO-8601, naive local time from datetime-local input. */
  appointmentAt: z.string().nullable().optional(),
  /** Amount the customer has already paid, in minor units. */
  amountPaidMinor: minorNonNegativeSchema.optional(),
  /**
   * Date that money arrived, as YYYY-MM-DD. Null means the amount is known but
   * the date is not, in which case the finance report leaves it bundled with
   * the final payment rather than guessing a month for it.
   */
  depositTakenAt: z.string().nullable().optional(),
  /** What the payment was, e.g. "Cash deposit on drop-off". */
  depositNote: z.string().trim().max(200).nullable().optional(),
  /** Optional appointment to link to this jobcard when creating it. */
  appointmentId: idSchema.nullable().optional(),
  lines: z.array(jobcardLineInputSchema).default([]),
})
export type JobcardCreate = z.infer<typeof jobcardCreateSchema>

/**
 * A full replacement of the jobcard's editable state. `lines` replaces the
 * entire line set in one transaction; partial line patching would need
 * client-side id bookkeeping for no benefit on a screen this size.
 */
export const jobcardUpdateSchema = z.object({
  /**
   * Attach, change or detach the customer.
   *
   * Optional so an ordinary save that does not mention the customer leaves it
   * alone; an explicit `null` detaches. This is how an unassigned jobcard gets
   * its owner once the workshop works out whose car it is.
   */
  clientId: idSchema.nullable().optional(),
  car: jobcardCarSelectionSchema.optional(),
  complaint: optionalText(2000),
  notes: optionalText(2000),
  odometerKm: z.number().int().min(0).max(10_000_000).nullable().default(null),
  vatEnabled: z.boolean(),
  discountMinor: minorNonNegativeSchema.default(0),
  /** Optional payment link set by staff. Null when unset. */
  paymentUrl: paymentUrlSchema.optional(),
  /** Customer's order or LPO reference. */
  orderRef: z.string().nullable().optional(),
  /** Appointment date and time. ISO-8601, naive local time from datetime-local input. */
  appointmentAt: z.string().nullable().optional(),
  /** Amount the customer has already paid, in minor units. */
  amountPaidMinor: minorNonNegativeSchema.optional(),
  /**
   * Date that money arrived, as YYYY-MM-DD. Null means the amount is known but
   * the date is not, in which case the finance report leaves it bundled with
   * the final payment rather than guessing a month for it.
   */
  depositTakenAt: z.string().nullable().optional(),
  /** What the payment was, e.g. "Cash deposit on drop-off". */
  depositNote: z.string().trim().max(200).nullable().optional(),
  lines: z.array(jobcardLineInputSchema),
})
export type JobcardUpdate = z.infer<typeof jobcardUpdateSchema>

export const jobcardTransitionSchema = z
  .object({
    action: jobcardActionSchema,
    /** Payment method when marking as paid. Required for mark_paid, ignored for other actions. */
    paymentMethod: paymentMethodSchema.optional(),
  })
  .superRefine((data, ctx) => {
    if (data.action === 'mark_paid' && !data.paymentMethod) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['paymentMethod'],
        message: 'Payment method is required when marking as paid',
      })
    }
  })
export type JobcardTransitionRequest = z.infer<typeof jobcardTransitionSchema>

export const jobcardSchema = z.object({
  id: idSchema,
  number: z.string(),
  clientId: idSchema.nullable(),
  carId: idSchema.nullable(),
  carSnapshot: carSnapshotSchema,
  status: jobcardStatusSchema,
  complaint: z.string().nullable(),
  notes: z.string().nullable(),
  odometerKm: z.number().int().nullable(),
  vatEnabled: z.boolean(),
  discountMinor: minorNonNegativeSchema,
  /** Snapshotted at creation so a later rate change never rewrites history. */
  vatRateBp: basisPointsSchema,
  /** Unguessable token for the customer-facing page link. */
  publicToken: z.string(),
  /** Optional payment link set by staff. */
  paymentUrl: z.string().nullable(),
  orderRef: z.string().nullable(),
  appointmentAt: z.string().nullable(),
  amountPaidMinor: minorNonNegativeSchema,
  depositTakenAt: z.string().nullable(),
  depositNote: z.string().nullable(),
  quotedAt: isoDateTimeSchema.nullable(),
  approvedAt: isoDateTimeSchema.nullable(),
  rejectedAt: isoDateTimeSchema.nullable(),
  startedAt: isoDateTimeSchema.nullable(),
  completedAt: isoDateTimeSchema.nullable(),
  invoicedAt: isoDateTimeSchema.nullable(),
  paidAt: isoDateTimeSchema.nullable(),
  cancelledAt: isoDateTimeSchema.nullable(),
  /** Payment method when marked paid. Snapshotted at mark_paid time. */
  paymentMethod: paymentMethodSchema.nullable().default(null),
  /** Fee charged for this payment method, in minor units. Snapshotted at mark_paid time. */
  paymentFeeMinor: minorNonNegativeSchema.default(0),
  /** Basis points rate snapshotted at mark_paid time (informational). */
  paymentFeeBp: basisPointsSchema.nullable().default(null),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
})
export type Jobcard = z.infer<typeof jobcardSchema>

/** A jobcard row as shown in the list: enough to render without a second call. */
export interface JobcardListItem {
  id: string
  number: string
  status: z.infer<typeof jobcardStatusSchema>
  clientId: string | null
  /** `clientDisplayName()`, or an empty string when nobody is attached yet. */
  clientName: string
  /** The linked car record, or null for a manually entered car. */
  carId: string | null
  carLabel: string
  plate: string
  /**
   * Shown when there is no plate. A car that arrived on a scanner has a VIN
   * and nothing else, and a row identifying it by neither is a row nobody can
   * match to the car in the yard.
   */
  vin: string
  subtotalMinor: number
  totalMinor: number
  /** What the parts/labour on this jobcard cost the workshop. Staff-only figure. */
  costMinor: number
  /** Things currently out of the workshop for this jobcard (repair/cleaning/...). Empty when nothing is out. Staff-only. */
  sentOut: TimelineSentOutBrief[]
  createdAt: string
  updatedAt: string
}

/** A jobcard's booking, for the calendar's workload view — not the full list row. */
export interface JobcardAppointment {
  id: string
  number: string
  status: z.infer<typeof jobcardStatusSchema>
  /** Empty when nobody is attached yet. */
  clientName: string
  carLabel: string
  plate: string
  /** Shown in place of a missing plate — see JobcardListItem for why. */
  vin: string
  /** "YYYY-MM-DDTHH:mm", as typed into the appointment field. */
  appointmentAt: string
}

/** Everything the jobcard editor needs, in one response. */
export interface JobcardDetail extends Jobcard {
  /** Null until a customer is attached. */
  client: Client | null
  lines: JobcardLine[]
  documents: DocumentSummary[]
  totals: Totals
  margin: Margin
}
